import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarCheck, BellOff, Sparkles, Search, Video, Trash2, ChevronRight, ChevronDown } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";
import { formatInZone } from '@/lib/serverTime';
import { timezoneForContact } from '@/lib/showTimezone';
import { useAuth } from '@/lib/AuthContext';
import { formatPhoneAsTyped } from '@/lib/phone';
import { capitalizeWords } from '@/lib/titleCase';
import MetOnField from '@/components/meeting/MetOnField';
import ReplacePhotoField from '@/components/meeting/ReplacePhotoField';
import ImageLightbox from '@/components/ImageLightbox';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { needsCrossOwnerConfirm, crossOwnerSendMessage, sendSmsToContact } from '@/lib/contactSend';
import { badgeStatus } from '@/lib/contactStatus';
import { ZOOM_SCHEDULER_FIELDS, ZOOM_MEETING_TOPIC } from '@/lib/zoomSchedulerFields';
import PageContainer from '@/components/PageContainer';

/**
 * Editing a contact after it was saved. A full page (task 3 of the
 * 2026-09-06 batch), not the floating dialog this used to be: the dialog's
 * close button was too small, and there was a separate read only "view"
 * dialog in front of it that most of this content used to live in.
 *
 * View mode is gone entirely (task 4). Everything that only used to exist
 * there now lives here: the Email Follow Ups list (which now also shows the
 * initial text, not just the four emails) and the Booked a meeting / Stop
 * reminders toggles. The rest of the old view dialog (a read only copy of
 * company, email, phone, website, meeting topics, internal notes, the Zoom
 * link) is not ported: every one of those fields already has a real,
 * editable input on this page, so a second read only copy would just be
 * stale the moment the field above it changes.
 *
 * Who owns a contact WAS deliberately not editable, on the reasoning that it
 * is a record of what happened rather than a preference. Contact
 * reassignment (2026-09-08) reverses that: an admin can change it, because
 * scanned_by_user_id is also what drives which Gmail sends pending follow
 * ups, which Sheet tab a personal capture lands in, and whose calendar link
 * gets offered, and a rep leaving or a workload rebalance needs all of that
 * to move together. It is a plain rep, not an admin, that still cannot
 * change it: reassignment is an admin only control, see the Owning Rep
 * field near the bottom of this page.
 */

/**
 * A ZoomInfo suggestion for a field this app never auto-applies (task:
 * ZoomInfo enrichment, agreed with the owner 2026-09-07). Renders nothing
 * when there is no suggestion, or the suggestion already matches what is
 * typed. One click copies it into the real field; the rep still has to hit
 * Save like any other edit, so nothing is written until they choose to.
 */
/**
 * email_source / phone_source (task: source labels, 2026-09-24). One of
 * exactly three stored values when set: renders that label and stops
 * there, since a real source always wins over guessing at one.
 *
 * Two further, computed (never stored) states for when source is null and
 * the field is still genuinely empty, "ZoomInfo: not yet researched" and
 * "Info not found on ZoomInfo" (task: 2026-09-24), telling apart the two
 * situations a null source alone could not: genuinely never checked yet
 * (still pending, the feature is off, or the wrong side of a real cutoff)
 * versus checked, matched a real person, and that person's ZoomInfo record
 * simply has nothing in this field. matchStatus === 'FULL_MATCH' is what "a
 * real match" means here, the same bar EditContact.jsx's own ZoomInfo-
 * checked line already applies a few dozen lines below (anything else,
 * including 'NO_MATCH', reads as "not yet researched", not as a confirmed
 * negative). A legacy contact can have a real value and a null source at
 * once (migration 0020 was never backfilled on purpose); stillEmpty is what
 * stops that value from ever being second-guessed just because nothing
 * recorded how it got there.
 *
 * zoomInfoTrackingEnabled gates both computed states behind whether this
 * field is something ZoomInfo is actually being asked about right now, not
 * just whether the UI happens to have the data plumbed through: it defaults
 * false precisely so a future field this app starts tracking but does not
 * yet request cannot silently pick up a computed label that would be
 * asserting something never actually checked, the exact trap Phone sat in
 * for most of 2026-09-24, after the directPhone incident meant
 * zoominfo_phone was unconditionally empty for every contact regardless of
 * whether ZoomInfo genuinely had a number. Both Email and Phone now pass
 * true: Phone's own field name, 'mobilePhone', was confirmed correct first
 * via an isolated diagnostic, then verified against real captures with real
 * results (2026-09-25), the same bar Email already met, not assumed just
 * because the code compiles.
 */
/**
 * Whether ZoomInfo has not yet produced a confirmed value for this field:
 * covers both "never attempted" and "attempted but no full match"
 * (LIMIT_EXCEEDED, NO_MATCH, a transport error that never completed) as one
 * boolean. Drives the manual "Look up on ZoomInfo" button's visibility
 * (task: 2026-09-25), which only needs to know whether there is anything
 * left to try, not which specific sub-state a field is in. See
 * zoomInfoFieldLabel below for the text split between those sub-states,
 * added 2026-09-25 after a real Jason Elwell contact showed "not yet
 * researched" next to a "Retry ZoomInfo lookup" button: enrichment HAD
 * completed (LIMIT_EXCEEDED, the account's own ZoomInfo credit balance was
 * at 0 of 1000), so the button was correct to say Retry, but the field text
 * was misleadingly identical to the genuinely-never-tried case.
 */
function isNotYetResearched({ source, currentValue, enrichedAt, matchStatus, zoomInfoTrackingEnabled }) {
  if (source) return false;
  if (!zoomInfoTrackingEnabled) return false;
  if ((currentValue || '').trim()) return false;
  return !(!!enrichedAt && matchStatus === 'FULL_MATCH');
}

/**
 * The specific text to show next to a field with no source of its own.
 * Splits what isNotYetResearched above treats as one boolean into the
 * sub-state a rep actually needs to see: a field that was never attempted
 * reads differently from one where a real attempt completed and hit the
 * ZoomInfo account's own credit limit (which will keep failing on retry
 * until the account resets, not a per-contact problem), which reads
 * differently again from a real attempt that simply had nothing for this
 * field. Returns null when there is nothing to show at all.
 */
function zoomInfoFieldLabel({ currentValue, zoominfoValue, enrichedAt, matchStatus, zoomInfoTrackingEnabled }) {
  if (!zoomInfoTrackingEnabled) return null;
  if ((currentValue || '').trim()) return null;

  if (!enrichedAt) return 'ZoomInfo: not yet researched';

  if (matchStatus === 'FULL_MATCH') {
    // Matched, but ZoomInfo genuinely had no suggestion for this specific
    // field: the Suggestion block below already shows a real suggestion
    // with its own "Use this", so this only fires when there is none.
    return (zoominfoValue || '').trim() ? null : 'Info not found on ZoomInfo';
  }

  if (matchStatus === 'LIMIT_EXCEEDED') return 'ZoomInfo: account limit reached';

  return 'ZoomInfo: no match found';
}

function SourceLabel({ source, currentValue, zoominfoValue, enrichedAt, matchStatus, zoomInfoTrackingEnabled = false }) {
  if (source) {
    const label = source === 'scanned' ? 'Scanned from business card'
      : source === 'zoominfo' ? 'Pulled from ZoomInfo'
      : 'Entered manually';
    return <span className="text-[10px] text-muted-foreground italic font-normal normal-case">{label}</span>;
  }

  const label = zoomInfoFieldLabel({ currentValue, zoominfoValue, enrichedAt, matchStatus, zoomInfoTrackingEnabled });
  if (!label) return null;
  return <span className="text-[10px] text-muted-foreground italic font-normal normal-case">{label}</span>;
}

/**
 * Manual, single-tap "Look up on ZoomInfo" / "Retry ZoomInfo lookup" button
 * (task: 2026-09-25). One button for the whole contact, not one per field:
 * a single enrich call populates email/title/company/phone/LinkedIn
 * together, so a per-field button would just be misleading about what one
 * click actually does. Shown whenever Email or Phone is currently showing
 * "ZoomInfo: not yet researched", which already covers both a contact that
 * has never been attempted and one that was attempted but got something
 * short of a full match (LIMIT_EXCEEDED, NO_MATCH, a transport error): a
 * rep should not need an admin for either case.
 */
function ZoomInfoLookupButton({ meeting, onLookup, loading }) {
  const emailNeedsLookup = isNotYetResearched({
    source: meeting.email_source,
    currentValue: meeting.email,
    enrichedAt: meeting.zoominfo_enriched_at,
    matchStatus: meeting.zoominfo_match_status,
    zoomInfoTrackingEnabled: true,
  });
  const phoneNeedsLookup = isNotYetResearched({
    source: meeting.phone_source,
    currentValue: meeting.phone,
    enrichedAt: meeting.zoominfo_enriched_at,
    matchStatus: meeting.zoominfo_match_status,
    zoomInfoTrackingEnabled: true,
  });
  if (!emailNeedsLookup && !phoneNeedsLookup) return null;

  const label = meeting.zoominfo_enriched_at ? 'Retry ZoomInfo lookup' : 'Look up on ZoomInfo';

  return (
    <button
      type="button"
      onClick={onLookup}
      disabled={loading}
      className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 disabled:opacity-50"
    >
      <Search className="w-3.5 h-3.5" />
      {loading ? 'Looking up...' : label}
    </button>
  );
}

function Suggestion({ label, value, current, onAccept }) {
  const suggestion = (value || '').trim();
  if (!suggestion) return null;
  if (suggestion.toLowerCase() === (current || '').trim().toLowerCase()) return null;

  return (
    <div className="flex items-center gap-2 mt-1.5 text-xs bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
      <Sparkles className="w-3.5 h-3.5 text-amber-600 shrink-0" />
      <span className="text-amber-900 flex-1 min-w-0 truncate">
        ZoomInfo suggests {label.toLowerCase()}: <span className="font-medium">{suggestion}</span>
      </span>
      <button
        type="button"
        onClick={() => onAccept(suggestion)}
        className="shrink-0 text-amber-700 font-semibold hover:text-amber-900 underline"
      >
        Use this
      </button>
    </div>
  );
}

/**
 * A LinkedIn URL a rep typed themselves has to be trusted (never overwritten
 * automatically), which is why this is completely separate from the real
 * linkedin_url field, and from the ZoomInfo Suggestion above it: visually
 * distinct (blue, not amber), its own coarse confidence label, never a
 * precise percentage since one would be invented. Same one click "Use this"
 * pattern; nothing is written until the rep chooses to.
 */
/**
 * Manual "Look up LinkedIn" button (task: 2026-09-25), for a contact with no
 * linkedin_url yet. Visibility is handled at the call site (the same
 * `!form.linkedin_url` condition LinkedInSuggestion's own usage already
 * wraps itself in below), not here, so both stay in lockstep with the exact
 * same check. Calls POST /api/contacts/:id/linkedin-lookup, web search only,
 * never ZoomInfo (narrowed the same day so this button never spends a
 * ZoomInfo credit): only ever produces an unverified suggestion, which
 * surfaces through the existing LinkedInSuggestion component right below,
 * once the contact refetches, not a new, separate UI.
 */
function LinkedInLookupButton({ onLookup, loading }) {
  return (
    <button
      type="button"
      onClick={onLookup}
      disabled={loading}
      className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-900 disabled:opacity-50"
    >
      <Search className="w-3.5 h-3.5" />
      {loading ? 'Looking up...' : 'Look up LinkedIn'}
    </button>
  );
}

function LinkedInSuggestion({ url, confidence, onAccept }) {
  if (!url) return null;
  const label = confidence === 'likely' ? 'Likely match' : 'Possible match';

  return (
    <div className="flex items-center gap-2 mt-1.5 text-xs bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5">
      <Search className="w-3.5 h-3.5 text-sky-600 shrink-0" />
      <span className="text-sky-900 flex-1 min-w-0 truncate">
        Web search, {label.toLowerCase()}:{' '}
        <a href={url} target="_blank" rel="noreferrer" className="underline font-medium">view profile</a>
      </span>
      <button
        type="button"
        onClick={() => onAccept(url)}
        className="shrink-0 text-sky-700 font-semibold hover:text-sky-900 underline"
      >
        Use this
      </button>
    </div>
  );
}

/**
 * Raw ZoomInfo company facts, task: build all four (2026-09-08). No AI
 * interpretation, just the numbers ZoomInfo returned, formatted for
 * readability (revenue is stored in thousands, converted here for display,
 * not re-estimated). Renders nothing until company enrichment has actually
 * run, and nothing at all if it came back with no match.
 */
function formatRevenue(thousands) {
  if (thousands == null) return null;
  const dollars = thousands * 1000;
  if (dollars >= 1_000_000_000) return `$${(dollars / 1_000_000_000).toFixed(1)}B`;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${Math.round(dollars / 1_000)}K`;
  return `$${dollars}`;
}

function CompanyPanel({ companyId }) {
  const { data: company } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => base44.companies.get(companyId),
    enabled: !!companyId,
  });

  if (!company || !company.zoominfo_enriched_at) return null;

  const facts = [
    company.location_count != null && { label: 'Locations', value: company.location_count },
    company.revenue != null && { label: 'Revenue', value: formatRevenue(company.revenue) },
    company.employee_count != null && { label: 'Employees', value: company.employee_count.toLocaleString() },
    company.founded_year != null && { label: 'Founded', value: company.founded_year },
    company.primary_industry && { label: 'Industry', value: company.primary_industry },
  ].filter(Boolean);

  const csuite = company.top_csuite || [];
  if (facts.length === 0 && !company.linkedin_url && csuite.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-2.5">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{company.name}</p>

      {facts.length > 0 && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          {facts.map(f => (
            <div key={f.label} className="text-xs">
              <span className="text-muted-foreground">{f.label}: </span>
              <span className="font-medium text-foreground">{f.value}</span>
            </div>
          ))}
        </div>
      )}
      {company.employee_count != null && (
        <p className="text-[10px] text-muted-foreground">Employee count includes store staff, not just corporate.</p>
      )}
      {company.linkedin_url && (
        <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">
          Company LinkedIn
        </a>
      )}

      {csuite.length > 0 && (
        <div className="pt-2 border-t border-border/50">
          <p className="text-[11px] font-medium text-muted-foreground mb-1">Other C-suite at this company</p>
          <div className="space-y-0.5">
            {csuite.map((exec, i) => (
              <p key={i} className="text-xs text-foreground">
                {exec.name}
                {exec.title ? <span className="text-muted-foreground"> ({exec.title})</span> : null}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The 5-touch send history (task: real sent content, not a generic
 * template preview, 2026-09-24), backed by GET /api/contacts/:id/touches
 * and worker/lib/contactTouches.ts, which decides everything shown here;
 * this component only renders what it is handed.
 *
 * A sent touch with real captured content (subject/body, migration 0021)
 * expands in place to show it: the SMS body as plain text, an email as a
 * sandboxed iframe, same `sandbox="allow-same-origin"` pattern
 * EventEmailEditor.jsx already uses for the admin's own template preview.
 * Unlike that preview, this is already-sent, immutable history, not a live
 * editing surface, so there is no auto-height measurement here, a fixed,
 * internally-scrollable box is enough for something collapsed by default.
 * A sent touch with no captured content (any real send from before this
 * shipped) says so plainly rather than pretending to be expandable.
 */
const PENDING_LABEL = {
  will_never_send: 'Not applicable',
  unknown: 'Not yet sendable',
  not_yet_sent: 'Not sent yet',
};

function TouchRow({ touch, expanded, onToggle, timezone }) {
  if (touch.status !== 'sent') {
    const rightLabel =
      touch.status === 'scheduled' && touch.expectedDate
        ? `Expected ${formatInZone(touch.expectedDate, timezone, 'EEE MMM d')}`
        : PENDING_LABEL[touch.status] || 'Not sent yet';
    return (
      <div className="text-xs py-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground">{touch.label}</span>
          <span className={touch.status === 'scheduled' ? 'text-foreground font-medium' : 'text-muted-foreground/60'}>
            {rightLabel}
          </span>
        </div>
        {touch.reason && <p className="text-[10px] text-muted-foreground/60 mt-0.5">{touch.reason}</p>}
      </div>
    );
  }

  const hasContent = !!(touch.subject || touch.body);
  const isEmail = touch.key !== 'initial_text';

  return (
    <div className="text-xs py-1">
      <button
        type="button"
        onClick={hasContent ? onToggle : undefined}
        disabled={!hasContent}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="text-muted-foreground flex items-center gap-1">
          {hasContent ? (
            expanded ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />
          ) : (
            <span className="w-3 shrink-0" />
          )}
          {touch.label}
        </span>
        <span className="text-foreground font-medium shrink-0">Sent {formatInZone(touch.sentAt, timezone, "EEE MMM d 'at' h:mm a zzz")}</span>
      </button>
      {!hasContent && <p className="text-[10px] text-muted-foreground/50 mt-0.5 pl-4">No saved copy of this message.</p>}
      {hasContent && expanded && (
        <div className="mt-1.5 ml-4 rounded-lg border border-border overflow-hidden">
          {touch.subject && (
            <div className="bg-secondary/40 px-2.5 py-1.5 border-b border-border">
              <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Subject</p>
              <p className="text-xs font-medium text-foreground">{touch.subject}</p>
            </div>
          )}
          {touch.body && (
            isEmail ? (
              <iframe
                title={`${touch.label}, sent ${touch.sentAt}`}
                sandbox="allow-same-origin"
                srcDoc={touch.body}
                className="w-full block bg-white"
                style={{ height: '16rem' }}
              />
            ) : (
              <p className="text-xs text-foreground whitespace-pre-wrap px-2.5 py-2">{touch.body}</p>
            )
          )}
        </div>
      )}
    </div>
  );
}

function TouchList({ touches, timezone }) {
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const toggle = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (touches.length === 0) return null;

  return (
    <div className="space-y-1">
      {touches.map((touch) => (
        <TouchRow key={touch.key} touch={touch} expanded={expandedKeys.has(touch.key)} onToggle={() => toggle(touch.key)} timezone={timezone} />
      ))}
    </div>
  );
}

/**
 * Only the fields this page owns, so nothing else on the contact is
 * touched. Phone and office phone are run through formatPhoneAsTyped here
 * too, not just on the input's own onChange: a contact captured before
 * that existed, or imported with a bare digit string, should display
 * formatted the moment this page opens, not only after someone happens to
 * retype it. Both `form` and `initialForm` come from this same function,
 * so the dirty check still starts equal, this is a display normalization,
 * not an edit.
 */
/**
 * Appends a contact's own name, email, company, title and phone to a
 * rep's calendar link as Zoom Scheduler's own documented prefill query
 * parameters (task: Zoom link prefill, 2026-09-16; extended to
 * title/phone/topic, 2026-09-16, second pass).
 *
 * firstname/lastname/email/companyname use the same parameter name on
 * every rep's page (confirmed against all four real pages), since those
 * are either Zoom's own standard fields or a custom one every rep happens
 * to have labeled identically. Title, phone and the meeting-topic field
 * are not standard, and each rep's own page has a different label for
 * them, or none at all, so there is no single parameter name that works
 * for everyone: repFieldMap (web/src/lib/zoomSchedulerFields.js, found
 * empirically per rep, not guessed) says which parameter name, if any,
 * this specific rep's page actually uses for each. A rep with no entry
 * for one of these three just does not get that parameter, same as a
 * contact with no value for a field: nothing sent, not an empty one Zoom
 * would otherwise show as a blank required field.
 *
 * Uses the URL API for encoding, not string concatenation: URLSearchParams
 * handles turning "@" into %40 and everything else that needs escaping,
 * so this never has to get that right by hand.
 *
 * If baseUrl is not a real absolute URL, or if the link is a rep's
 * WordPress wrapper page rather than the raw Zoom URL underneath it
 * (this project's own known case, worker/lib/messages.ts's docs on the
 * same links), this still returns a valid URL, just without the
 * parameters ever reaching Zoom's own form. That degradation is
 * deliberate: the button must still open the rep's real calendar link
 * either way, never error out just because prefill will not work for it.
 */
function buildZoomScheduleUrl(baseUrl, contact, repFieldMap = {}) {
  let url;
  try {
    url = new URL(baseUrl);
  } catch {
    return baseUrl;
  }

  const fields = {
    firstname: contact?.first_name,
    lastname: contact?.last_name,
    email: contact?.email,
    companyname: contact?.company,
  };
  if (repFieldMap.title) fields[repFieldMap.title] = contact?.title;
  if (repFieldMap.phone) fields[repFieldMap.phone] = contact?.phone;
  if (repFieldMap.topic) fields[repFieldMap.topic] = ZOOM_MEETING_TOPIC;

  for (const [param, value] of Object.entries(fields)) {
    if (value && value.trim()) url.searchParams.set(param, value.trim());
  }
  return url.toString();
}

function formFrom(meeting) {
  return {
    first_name: meeting?.first_name || '',
    last_name: meeting?.last_name || '',
    company: meeting?.company || '',
    title: meeting?.title || '',
    email: meeting?.email || '',
    phone: formatPhoneAsTyped(meeting?.phone || ''),
    office_phone: formatPhoneAsTyped(meeting?.office_phone || ''),
    office_phone_ext: meeting?.office_phone_ext || '',
    website: meeting?.website || '',
    linkedin_url: meeting?.linkedin_url || '',
    reputation_scorecard_url: meeting?.reputation_scorecard_url || '',
    meeting_topics: meeting?.meeting_topics || '',
    internal_notes: meeting?.internal_notes || '',
    email_note: meeting?.email_note || '',
    captured_at: meeting?.captured_at || '',
    trade_show_id: meeting?.trade_show_id || '',
    badge_image_key: meeting?.badge_image_key || '',
    badge_back_image_key: meeting?.badge_back_image_key || '',
    person_photo_key: meeting?.person_photo_key || '',
  };
}

export default function EditContact() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();

  const { data: meeting, isLoading } = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => base44.entities.MeetingRequest.get(id),
  });

  // The 5-touch send history, task: real sent content, not a generic
  // template preview, 2026-09-24. Its own query, not folded into `meeting`
  // above: it needs its own DB round trips (the show's end date, the
  // reminders_enabled setting, outbound_log) that the rest of this page has
  // no other use for, so there is no reason to pay for them on every
  // contact fetch, only when this page is actually open.
  const { data: touchesData } = useQuery({
    queryKey: ['contact-touches', id],
    queryFn: () => base44.outbound.getTouches(id),
    enabled: !!id,
  });
  const touches = touchesData?.touches ?? [];

  const [saving, setSaving] = useState(false);
  // Task: photo lightbox on Edit Contact, 2026-09-24. One piece of state for
  // all three photos on this page (badge front, badge back, person), same
  // pattern ContactRow.jsx already uses for its own two: only one can be
  // open at a time, and null means the overlay renders nothing.
  const [lightbox, setLightbox] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [form, setForm] = useState(() => formFrom(meeting));
  const [initialForm, setInitialForm] = useState(() => formFrom(meeting));
  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  useUnsavedChangesGuard(isDirty);
  const { setGuard, guardedAction } = useNavigationGuard();

  // Optimistic local copies of the two toggles (task 6): flip instantly on
  // tap, PATCH in the background, revert and toast on failure, rather than
  // waiting on invalidateQueries and a refetch before the switch visually
  // moves. Kept separate from `form` since they save immediately on tap,
  // unlike every other field here which waits for the Save button.
  const [meetingBooked, setMeetingBooked] = useState(!!meeting?.meeting_booked);
  const [remindersStopped, setRemindersStopped] = useState(!!meeting?.reminders_stopped);
  const [savingToggle, setSavingToggle] = useState(null); // 'meeting_booked' | 'reminders_stopped' | null

  // Per contact owner change, task: contact reassignment (2026-09-08). Admin
  // only, and immediate like the two toggles above rather than bundled into
  // the Save Changes payload: the Event field below always sends show_id,
  // which the API refuses alongside a scanned_by_user_id change (it cannot
  // tell which show should win), so this has to be its own request.
  const [reassigning, setReassigning] = useState(false);
  const { data: reps = [] } = useQuery({
    queryKey: ['reps'],
    queryFn: async () => {
      try {
        const all = await base44.entities.User.list();
        return all.filter(u => u.status === 'active' && !u.is_device_account);
      } catch {
        return [];
      }
    },
    enabled: isAdmin,
  });

  const { data: shows = [] } = useQuery({
    queryKey: ['shows'],
    queryFn: () => base44.entities.TradeShow.list('-start_date', 50),
  });

  const selectedShow = shows.find(s => s.id === form.trade_show_id) || null;

  useEffect(() => {
    if (meeting) {
      const loaded = formFrom(meeting);
      setForm(loaded);
      setInitialForm(loaded);
      setMeetingBooked(!!meeting.meeting_booked);
      setRemindersStopped(!!meeting.reminders_stopped);
    }
  }, [meeting]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (field, value) => handleChange(field, formatPhoneAsTyped(value));

  const goBack = () => guardedAction(() => navigate(-1));

  // "Booked a meeting" also turns on "Stop reminders" (task 5, enforced
  // again server side in worker/routes/contacts.ts so it holds regardless
  // of what calls this route). Turning Booked back off leaves Stop
  // reminders exactly as it is: resuming the drip is a separate, deliberate
  // action, not implied by un-booking.
  const handleToggle = async (field, value) => {
    const previous = { meetingBooked, remindersStopped };
    setSavingToggle(field);

    if (field === 'meeting_booked') {
      setMeetingBooked(value);
      if (value) setRemindersStopped(true);
    } else {
      setRemindersStopped(value);
    }

    try {
      const payload = field === 'meeting_booked' && value
        ? { meeting_booked: true, reminders_stopped: true }
        : { [field]: value };
      await base44.entities.MeetingRequest.update(id, payload);
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      toast.success(
        field === 'meeting_booked'
          ? (value ? 'Marked as booked. Reminders will stop.' : 'Unmarked as booked.')
          : (value ? 'Reminders stopped.' : 'Reminders will resume on schedule.')
      );
    } catch (err) {
      setMeetingBooked(previous.meetingBooked);
      setRemindersStopped(previous.remindersStopped);
      toast.error(err.message || 'Could not update that.');
    } finally {
      setSavingToggle(null);
    }
  };

  const handleReassign = async (newOwnerId) => {
    if (newOwnerId === meeting.scanned_by_user_id) return;
    const newOwner = reps.find(r => r.id === newOwnerId);
    const confirmed = window.confirm(
      `Reassign this contact to ${newOwner?.full_name || 'this rep'}? ` +
      `This stops the pending follow up reminders, since ${newOwner?.full_name || 'they'} never met this person. ` +
      `They can resume from Stop Reminders below.`
    );
    if (!confirmed) return;

    setReassigning(true);
    try {
      await base44.entities.MeetingRequest.update(id, { scanned_by_user_id: newOwnerId });
      toast.success(`Reassigned to ${newOwner?.full_name || 'the new rep'}. Reminders stopped.`);

      // A personal bucket contact moved to somebody else's bucket becomes
      // invisible to this admin too, the same privacy rule that already
      // hides one rep's personal captures from another (lib/contacts.ts's
      // VISIBLE_TO_USER). Refetching this page would just 404. Leaving
      // instead of showing "No such contact" right after a save that worked.
      if (meeting.show_is_general && newOwnerId !== user?.id) {
        queryClient.invalidateQueries({ queryKey: ['meetings'] });
        navigate(-1);
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
    } catch (err) {
      toast.error(err.message || 'Could not reassign that contact.');
    } finally {
      setReassigning(false);
    }
  };

  // The TradeSync templated send, moved here from the Home contact row
  // (task: contact row redesign, 2026-09-08). Two independent buttons, not
  // a combined "send both": a rep might want to trigger only one channel,
  // same as the old per-channel checkboxes this replaces.
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);

  const handleTemplatedSend = async (channel) => {
    // Cross-owner confirmation, task: rework how the welcome text and
    // email are sent (2026-09-23). This is the one screen where "current
    // user" and "contact owner" can actually diverge (any rep or admin can
    // open any contact's detail page, show silos being off), unlike the
    // creation screen, where a contact can only ever be created owned by
    // whoever confirmed capturing it.
    if (needsCrossOwnerConfirm(user, meeting)) {
      const proceed = window.confirm(
        crossOwnerSendMessage(meeting.scanned_by_name, { email: channel === 'email', sms: channel === 'sms' })
      );
      if (!proceed) return;
    }

    const setSending = channel === 'email' ? setSendingEmail : setSendingSms;
    setSending(true);
    try {
      if (channel === 'email') {
        await base44.outbound.sendEmail(id);
        toast.success('Email sent.');
      } else {
        // shouldUseNativeSmsHandoff (inside sendSmsToContact) covers native
        // handoff vs Twilio: only the contact's own owner, on their own
        // mobile phone, not the shared device account. A confirmed
        // cross-owner send above always falls through to Twilio here,
        // matching what the confirmation copy itself just told the caller.
        const result = await sendSmsToContact(user, meeting);
        toast.success(result.handoff ? 'Opened Messages.' : 'Text sent.');
      }
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      queryClient.invalidateQueries({ queryKey: ['contact-touches', id] });
    } catch (err) {
      toast.error(err.message || `Failed to send the ${channel === 'email' ? 'email' : 'text'}.`);
    } finally {
      setSending(false);
    }
  };

  // Manual "Look up on ZoomInfo" / "Retry ZoomInfo lookup" button (task:
  // 2026-09-25). Runs synchronously on the server and resolves with a real
  // answer, unlike the automatic capture-time call, which is fire-and-forget
  // with nothing to report back to.
  const [zoomInfoLookingUp, setZoomInfoLookingUp] = useState(false);

  const handleZoomInfoLookup = async () => {
    setZoomInfoLookingUp(true);
    try {
      const result = await base44.entities.MeetingRequest.zoominfoLookup(id);
      if (!result.zoominfo?.completed) {
        toast.error('ZoomInfo did not respond. Safe to try again.');
      } else if (result.zoominfo.matchStatus === 'FULL_MATCH') {
        toast.success('ZoomInfo found a match.');
      } else if (result.zoominfo.matchStatus === 'LIMIT_EXCEEDED') {
        // Distinct from the plain no-match toast below (task: 2026-09-25,
        // after a real contact got two identical taps in a row because the
        // generic "no match found" toast gave no reason not to try again).
        // This is an account-wide credit limit, not something specific to
        // this contact: retrying will not produce a different result until
        // the ZoomInfo account's own balance resets.
        toast.error('ZoomInfo has hit its account credit limit. Retrying will not help right now.');
      } else {
        toast.success('ZoomInfo checked, no match found.');
      }
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
    } catch (err) {
      toast.error(err.message || 'Could not look that up on ZoomInfo.');
    } finally {
      setZoomInfoLookingUp(false);
    }
  };

  // Manual "Look up LinkedIn" button (task: 2026-09-25). Same shape as the
  // ZoomInfo lookup above: runs synchronously on the server and resolves
  // with a real answer.
  const [linkedinLookingUp, setLinkedinLookingUp] = useState(false);

  const handleLinkedInLookup = async () => {
    setLinkedinLookingUp(true);
    try {
      // Web search only (task: 2026-09-25, narrowed the same day so this
      // button never spends a ZoomInfo credit): only ever a suggestion to
      // report, never a direct match. Nothing to write into form here:
      // LinkedInSuggestion below reads straight from the meeting query, so
      // invalidating it is enough for the suggestion to appear.
      const result = await base44.entities.MeetingRequest.linkedinLookup(id);
      if (result.linkedin?.foundSuggestion) {
        toast.success('LinkedIn suggestion found, review below.');
      } else {
        toast.error('No LinkedIn profile found.');
      }
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
    } catch (err) {
      toast.error(err.message || 'Could not look up LinkedIn.');
    } finally {
      setLinkedinLookingUp(false);
    }
  };

  // The signed in rep's own calendar links, not the contact's owning rep
  // specifically: GET /api/reps (what a non-admin can call) does not
  // return calendar_links at all, only an admin's GET /api/users does, and
  // building a second, admin-only path just for this was out of scope
  // here. In the overwhelmingly common case, a rep is looking at their own
  // contact anyway, so this is the same link they would pick at capture
  // time.
  const repCalendarLinks = user?.calendar_links || [];
  const defaultCalendarLink = repCalendarLinks.find((l) => l.is_default) || repCalendarLinks[0] || null;

  // Which link the picker below currently has selected, task: two separate
  // controls, not one combined dropdown-that-also-opens, 2026-09-16.
  // Defaults to the rep's own default the moment their profile actually
  // loads (user arrives async), the same is_default-first fallback
  // NewMeeting.jsx's own picker already uses at capture time. Picking a
  // different option here only ever changes this value; it does not open
  // anything, that is the calendar button's job alone.
  const [selectedLinkId, setSelectedLinkId] = useState(null);
  useEffect(() => {
    if (selectedLinkId || !defaultCalendarLink) return;
    setSelectedLinkId(defaultCalendarLink.id);
  }, [defaultCalendarLink?.id]);
  const selectedCalendarLink = repCalendarLinks.find((l) => l.id === selectedLinkId) || defaultCalendarLink;

  // Works around a real Zoom platform limit, not this app's own (task:
  // copy cell phone to clipboard, 2026-09-16): Adam's and Mike's "Best
  // Contact Phone #" field on their own booking pages is Zoom's structured
  // phone field type, which cannot be prefilled via URL parameter at all
  // (confirmed by testing many candidate names against it,
  // zoomSchedulerFields.js's own notes); Marklen's equivalent field works
  // because hers happens to be a plain text field instead. Copying the
  // number to the clipboard here means a rep can still paste it into that
  // field by hand in one motion, rather than typing it out from the
  // contact page.
  const handleScheduleClick = () => {
    if (meeting.phone) {
      try {
        // Deliberately not awaited: awaiting this before window.open below
        // would risk Safari and Chrome treating the tab as no longer
        // opened from a trusted user gesture (a synchronous click handler
        // interrupted by an awaited promise is exactly what triggers
        // popup blocking), which would silently break the actual
        // scheduling link. Starting the write here, then opening the tab
        // immediately after without waiting on it, keeps both firing from
        // the same click without that risk.
        //
        // No confirmation toast on success (task: rename the button,
        // 2026-09-16): the button's own label, "Copy phone # and schedule
        // meeting", already tells the rep what tapping it does, so a
        // toast on top of that would just be a redundant second copy of
        // the same information.
        navigator.clipboard.writeText(meeting.phone).catch(() => {
          // Some browser contexts (Safari in particular, depending on
          // permission state) refuse clipboard writes; the scheduling
          // link must still open either way, so this is deliberately
          // silent rather than surfaced as an error.
        });
      } catch {
        // navigator.clipboard can be entirely absent in some contexts
        // (non-HTTPS, certain embedded webviews); same rule, never let
        // that stop the link from opening.
      }
    }
    const repFieldMap = ZOOM_SCHEDULER_FIELDS[user?.email] || {};
    const url = buildZoomScheduleUrl(selectedCalendarLink.url, meeting, repFieldMap);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Split from the button's own click handler, task: unsaved changes guard
  // (2026-09-08): "Save and leave" in the guard dialog needs to run the
  // actual save and know whether it worked, without also triggering this
  // page's own navigate(-1), since the guard has its own, different
  // destination in mind (wherever the rep was actually trying to go).
  // Returns true only on a real success, which is what tells the guard it
  // is safe to leave.
  // No client side name check: a contact can be nameless (offline capture,
  // no OCR to run yet), and a rep adding a note or a phone number to one
  // must not be blocked from saving just because the name is still blank.
  // The server still refuses to blank out a name that already exists
  // (worker/routes/contacts.ts's PATCH handler), surfaced here through the
  // same catch below as any other save error.
  const doSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.trade_show_id) delete payload.trade_show_id;
      if (!payload.captured_at) delete payload.captured_at;

      await base44.entities.MeetingRequest.update(id, payload);
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['all-meetings-for-shows'] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      toast.success('Contact updated');
      return true;
    } catch (err) {
      toast.error(err.message || 'Failed to update contact');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const ok = await doSave();
    if (ok) navigate(-1);
  };

  // Soft delete, labeled "Archive" to the rep (task: restore the missing
  // delete entry point, 2026-09-23; renamed 2026-09-23). Routed through the
  // same update(id, { status: 'deleted' }) call the now orphaned
  // MeetingCard.jsx used, which base44Client.js already turns into the real
  // DELETE /api/contacts/:id (soft delete, restorable from Deleted
  // Contacts, never a new API call) -- the underlying status value and API
  // call are unchanged, only the copy shown to the rep says "archive"
  // rather than "delete". window.confirm rather than a new Dialog
  // component, the same pattern handleReassign above already uses for a
  // different destructive-ish action on this same page. Skips the unsaved
  // changes guard on purpose: the contact this page was editing is gone
  // either way, so there is nothing left to warn about, and the guard
  // clears itself on unmount regardless.
  const handleArchive = async () => {
    const name = meeting.first_name?.trim() ? `${meeting.first_name} ${meeting.last_name}` : 'this contact';
    const confirmed = window.confirm(
      `Archive ${name}? This removes them from Home's active list. You can restore them later from Deleted Contacts.`
    );
    if (!confirmed) return;

    setArchiving(true);
    try {
      await base44.entities.MeetingRequest.update(id, { status: 'deleted' });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['all-meetings-for-shows'] });
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      toast.success('Contact archived');
      navigate(-1);
    } catch (err) {
      toast.error(err.message || 'Failed to archive contact');
    } finally {
      setArchiving(false);
    }
  };

  // Registers this page's dirty state and save function with the global
  // guard (App.jsx's AppShellLayout), so the bottom nav's Home/+/More can
  // intercept navigation away from here too, not just this page's own back
  // arrow and Cancel. Cleared on unmount so a guard from a page the rep
  // already left never blocks navigation on the next one.
  useEffect(() => {
    setGuard(isDirty, doSave);
    return () => setGuard(false, null);
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-foreground font-medium">No such contact.</p>
        <Link to="/"><Button variant="outline">Back to Home</Button></Link>
      </div>
    );
  }

  // Same fallback as the Home contact row: coalesce to created_at, matching
  // the server's own sort order, for the handful of imported contacts with
  // no captured_at at all.
  const capturedDisplay = meeting.captured_at || meeting.created_at;

  // Every date shown on this page for this contact (captured, ZoomInfo
  // checked, each touch sent/expected) uses this same contact's own show
  // timezone (task: 2026-09-25), same helper ContactRow.jsx's Home row
  // already used for its own badge date/time, so a rep reviewing one
  // contact's whole history sees one consistent zone throughout, not a mix
  // of the show's zone and a blanket America/Chicago left over from before
  // shows outside Texas were a real scenario.
  const meetingTimezone = timezoneForContact(meeting);

  return (
    <div className="min-h-screen bg-background safe-top pb-24">
      <PageContainer className="px-4 py-6">
        {/* Save button placement, task: save button placement (2026-09-08).
            Back and Save in one row at the top, above the name, not in the
            bottom nav: the nav has to mean the same thing on every screen,
            and a Save slot there would displace the plus button. Back goes
            through the same guardedAction as the nav's own Home/+/More, so
            an unsaved change is caught no matter which the rep taps.
            Global "Back" button (task: 2026-09-25): this used to be a small
            round arrow icon with a separate "Cancel" button further along
            the same row, both calling this identical goBack handler. Cancel
            is gone now that Back itself, in the same visual style Cancel
            used to have, already does that job; keeping both would have
            been two differently labeled buttons doing the same thing. */}
        <div className="flex items-center gap-2 mb-4">
          <Button variant="outline" onClick={goBack} className="min-h-9 px-4 text-sm">Back</Button>
          <div className="flex-1" />
          <Button onClick={handleSave} disabled={saving || !isDirty} className="min-h-9 px-5 text-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>

        {/* Name is the dominant heading here, task: contact detail page
            (2026-09-08), roughly double its previous size, "Edit Contact"
            demoted to a small label above it. */}
        <div className="mb-6">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Edit Contact</p>
          <h1 className={`text-2xl font-bold truncate ${meeting.first_name?.trim() ? 'text-foreground' : 'text-muted-foreground italic'}`}>
            {meeting.first_name?.trim() ? `${meeting.first_name} ${meeting.last_name}` : 'Unnamed contact'}
          </h1>
          {capturedDisplay && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Captured {formatInZone(capturedDisplay, meetingTimezone, "EEE MMM d 'at' h:mm a zzz")}
            </p>
          )}
          {badgeStatus(meeting) === 'unreadable' && (
            <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-0.5">
              Badge unreadable, needs details typed in by hand
            </p>
          )}
        </div>

        <div className="space-y-5">
          <div className="pt-1 space-y-3">
            <ReplacePhotoField
              label="Badge photo"
              kind="badge"
              value={form.badge_image_key}
              original={meeting.badge_image_key || ''}
              onChange={v => handleChange('badge_image_key', v)}
              disabled={saving}
              onOpenLightbox={setLightbox}
            />
            <ReplacePhotoField
              label="Badge photo (back)"
              kind="badge"
              value={form.badge_back_image_key}
              original={meeting.badge_back_image_key || ''}
              onChange={v => handleChange('badge_back_image_key', v)}
              disabled={saving}
              onOpenLightbox={setLightbox}
            />
            <ReplacePhotoField
              label="Photo of them"
              kind="person"
              value={form.person_photo_key}
              original={meeting.person_photo_key || ''}
              onChange={v => handleChange('person_photo_key', v)}
              disabled={saving}
              onOpenLightbox={setLightbox}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">First Name</Label>
              <Input
                value={form.first_name}
                onChange={e => handleChange('first_name', e.target.value)}
                autoComplete="given-name"
                className="h-10 md:text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Last Name</Label>
              <Input
                value={form.last_name}
                onChange={e => handleChange('last_name', e.target.value)}
                autoComplete="family-name"
                className="h-10 md:text-sm mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Company</Label>
            <Input
              value={form.company}
              onChange={e => handleChange('company', e.target.value)}
              autoCapitalize="none"
              autoComplete="organization"
              className="h-10 md:text-sm mt-1"
            />
            <Suggestion
              label="Company"
              value={meeting.zoominfo_company_name}
              current={form.company}
              onAccept={(v) => handleChange('company', v)}
            />
          </div>

          <CompanyPanel companyId={meeting.company_id} />

          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={form.title}
              onChange={e => handleChange('title', capitalizeWords(e.target.value))}
              autoComplete="organization-title"
              className="h-10 md:text-sm mt-1"
            />
            <Suggestion
              label="Title"
              value={meeting.zoominfo_job_title}
              current={form.title}
              onAccept={(v) => handleChange('title', v)}
            />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1.5">
              Email
              <SourceLabel
                source={meeting.email_source}
                currentValue={meeting.email}
                zoominfoValue={meeting.zoominfo_email}
                enrichedAt={meeting.zoominfo_enriched_at}
                matchStatus={meeting.zoominfo_match_status}
                zoomInfoTrackingEnabled
              />
            </Label>
            <Input
              type="email"
              value={form.email}
              onChange={e => handleChange('email', e.target.value)}
              autoComplete="email"
              inputMode="email"
              className="h-10 md:text-sm mt-1"
            />
            <Suggestion
              label="Email"
              value={meeting.zoominfo_email}
              current={form.email}
              onAccept={(v) => handleChange('email', v)}
            />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1.5">
              Phone
              {/* zoomInfoTrackingEnabled turned on 2026-09-25: phone
                  requests resumed with 'mobilePhone' (see
                  worker/lib/zoominfo.ts's OUTPUT_FIELDS comment for the
                  full history), verified against real captures with real
                  results the same day, unlike the two earlier field-name
                  guesses. Same 4-state logic as Email now, no longer a
                  special case. */}
              <SourceLabel
                source={meeting.phone_source}
                currentValue={meeting.phone}
                zoominfoValue={meeting.zoominfo_phone}
                enrichedAt={meeting.zoominfo_enriched_at}
                matchStatus={meeting.zoominfo_match_status}
                zoomInfoTrackingEnabled
              />
            </Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={e => handlePhoneChange('phone', e.target.value)}
              autoComplete="mobile tel"
              className="h-10 md:text-sm mt-1"
            />
            {/* value is pre-formatted, not the raw suggestion string, unlike
                every other Suggestion on this page: Suggestion hides itself
                once `value` textually matches `current`, and phone is the
                one field whose typed input (handlePhoneChange) reformats
                on every keystroke. Comparing a raw ZoomInfo string like
                "2142931100" against the "(214) 293-1100" current would
                never match post-accept, leaving the suggestion showing
                forever even after being used. */}
            <Suggestion
              label="Phone"
              value={formatPhoneAsTyped(meeting.zoominfo_phone)}
              current={form.phone}
              onAccept={(v) => handleChange('phone', v)}
            />
            <ZoomInfoLookupButton meeting={meeting} onLookup={handleZoomInfoLookup} loading={zoomInfoLookingUp} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Office Phone</Label>
              <Input
                type="tel"
                value={form.office_phone}
                onChange={e => handlePhoneChange('office_phone', e.target.value)}
                autoComplete="work tel"
                className="h-10 md:text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Ext</Label>
              <Input value={form.office_phone_ext} onChange={e => handleChange('office_phone_ext', e.target.value)} className="h-10 md:text-sm mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Website</Label>
            <Input type="url" value={form.website} onChange={e => handleChange('website', e.target.value)} className="h-10 md:text-sm mt-1" />
          </div>
          <div>
            <Label className="text-xs">LinkedIn</Label>
            <Input
              type="url"
              value={form.linkedin_url}
              onChange={e => handleChange('linkedin_url', e.target.value)}
              placeholder="Filled in automatically when ZoomInfo finds one"
              className="h-10 md:text-sm mt-1"
            />
            {!form.linkedin_url && (
              <LinkedInLookupButton onLookup={handleLinkedInLookup} loading={linkedinLookingUp} />
            )}
            {meeting.zoominfo_enriched_at && (
              <p className="text-[10px] text-muted-foreground mt-1">
                ZoomInfo checked {formatInZone(meeting.zoominfo_enriched_at, meetingTimezone, "EEE MMM d 'at' h:mm a zzz")}:{' '}
                {meeting.zoominfo_match_status === 'FULL_MATCH'
                  ? `matched${meeting.zoominfo_accuracy_score ? `, ${meeting.zoominfo_accuracy_score}% accuracy` : ''}.`
                  : 'no match found.'}
              </p>
            )}
            {!form.linkedin_url && (
              <LinkedInSuggestion
                url={meeting.linkedin_suggestion_url}
                confidence={meeting.linkedin_suggestion_confidence}
                onAccept={(v) => handleChange('linkedin_url', v)}
              />
            )}
          </div>
          <div>
            <Label className="text-xs">Reputation Scorecard</Label>
            <Input
              type="url"
              value={form.reputation_scorecard_url}
              onChange={e => handleChange('reputation_scorecard_url', e.target.value)}
              placeholder="https://scorecard.example.com/..."
              className="h-10 md:text-sm mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Meeting Topics</Label>
            <p className="text-[11px] text-muted-foreground mb-1">Included in the follow up email</p>
            <Textarea value={form.meeting_topics} onChange={e => handleChange('meeting_topics', e.target.value)} className="md:text-sm" rows={2} />
          </div>
          <div>
            <Label className="text-xs">Internal Notes</Label>
            <p className="text-[11px] text-muted-foreground mb-1">Only for you</p>
            <Textarea value={form.internal_notes} onChange={e => handleChange('internal_notes', e.target.value)} className="md:text-sm" rows={2} />
          </div>
          <div>
            <Label className="text-xs">Note for Emails</Label>
            <Textarea value={form.email_note} onChange={e => handleChange('email_note', e.target.value)} className="md:text-sm" rows={2} />
            <p className="text-[10px] text-muted-foreground mt-1">
              Read fresh each time an email goes out, so this only changes emails not yet sent.
            </p>
          </div>

          <div className="pt-1 border-t border-border/50">
            <Label className="text-xs">Event</Label>
            <Select value={form.trade_show_id || ''} onValueChange={v => handleChange('trade_show_id', v)}>
              <SelectTrigger className="h-10 md:text-sm mt-1">
                <SelectValue placeholder="Not filed under an event" />
              </SelectTrigger>
              <SelectContent>
                {shows.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}{s.is_general ? ' (personal)' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Moving a contact here also changes the event named in the follow up.
            </p>
          </div>

          {/* No wrapping Label here (task: fix duplicate "Met on", 2026-09-23):
              MetOnField already renders its own "Met On" label internally, so
              a second one here just repeated it. */}
          <div>
            <MetOnField value={form.captured_at} onChange={v => handleChange('captured_at', v)} show={selectedShow} />
          </div>

          {/* Merged in from the old view dialog (task 4) */}
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email &amp; Text Follow Ups</p>

            {/* The TradeSync templated send, moved off the Home row
                (2026-09-08). Distinct from the plain Call/Text/Email
                buttons on the row, which just open the phone's own apps. */}
            <div className="flex gap-2">
              <Button
                onClick={() => handleTemplatedSend('email')}
                disabled={!meeting.email || sendingEmail}
                className="flex-1 h-9 text-xs font-semibold"
              >
                {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (meeting.email_sent_at ? 'Resend Email' : 'Send Email')}
              </Button>
              <Button
                onClick={() => handleTemplatedSend('sms')}
                disabled={!meeting.phone || sendingSms}
                className="flex-1 h-9 text-xs font-semibold"
              >
                {sendingSms ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (meeting.sms_sent_at ? 'Resend SMS' : 'Send SMS')}
              </Button>
            </div>

            {/* Two separate controls, task: build it correctly this time,
                2026-09-16, not one combined dropdown-that-also-opens (an
                earlier version of this conflated the two, and a stray tap
                on the dropdown itself could open the wrong link before a
                rep meant to). The select only ever changes which link is
                selected; only the button below opens anything, and only
                when actually tapped.

                Stacked, not side by side (task: design A, 2026-09-16): the
                button is now the primary, full-width action, with the
                dropdown underneath it rather than squeezed into the same
                row. */}
            {/* min-w-0 on the select below (task: fix a real horizontal
                scroll bug, 2026-09-16) is a deliberate, permanent fix, not
                leftover from the layout above. Diagnosed on a real iPhone,
                not reproducible in this project's own headless Chrome
                testing: an earlier version of this control sat side by
                side with the schedule button in a flex row, and a flex
                item's default min-width is auto, not 0. Confirmed by
                reading this exact element's own computed style: with a
                long link name ("30-Minute Meeting Mike and Adam"), Chrome
                already showed min-width: auto and white-space: nowrap on
                this trigger, the precise combination Safari (unlike
                Chrome, which computes a more forgiving automatic minimum
                for a native <button>-based control like this one) refuses
                to shrink below, pushing the row, and the whole page,
                wider than the viewport. Stacking the controls above
                already removes the flex row this depended on, but this
                stays anyway as the actual, permanent fix for the
                underlying cause, the same min-w-0 pattern already used
                elsewhere on this exact page (the ZoomInfo suggestion rows
                above) for an identical reason, rather than something a
                future layout change could silently reintroduce. */}
            <div className="space-y-2">
              {/* Opens the currently selected link below with this
                  contact's name, email, company and title/phone/topic
                  (wherever that rep's own page supports it,
                  zoomSchedulerFields.js) pre-filled as Zoom Scheduler's own
                  query parameters, and copies the contact's cell phone to
                  the clipboard first when Zoom's own field for it cannot
                  be prefilled (handleScheduleClick's own comment). Never
                  errors out for a link that is still a WordPress wrapper
                  page rather than the raw scheduler.zoom.us URL underneath
                  it (buildZoomScheduleUrl's own docs): it just opens
                  normally, without the prefill actually landing.

                  Label depends on whether there is actually a phone
                  number to copy (task: rename the button, 2026-09-16):
                  "Copy phone # and schedule meeting" would overpromise for
                  a contact with none, so that case gets the shorter,
                  accurate "Schedule meeting" instead. Confirmed with the
                  owner rather than guessed. */}
              <Button
                type="button"
                onClick={handleScheduleClick}
                disabled={!selectedCalendarLink}
                className="w-full min-h-11 text-sm font-semibold bg-accent hover:bg-accent/90 text-accent-foreground"
                title={selectedCalendarLink ? `Schedule with ${meeting.first_name?.trim() || 'this contact'}` : 'Set a calendar link on your profile first.'}
              >
                <Video className="w-4 h-4 mr-2" />
                {meeting.phone ? 'Copy phone # and schedule meeting' : 'Schedule meeting'}
              </Button>
              {repCalendarLinks.length > 1 && (
                <Select value={selectedLinkId || ''} onValueChange={setSelectedLinkId}>
                  <SelectTrigger className="h-9 w-full min-w-0 text-xs">
                    <SelectValue placeholder="Calendar link" />
                  </SelectTrigger>
                  <SelectContent>
                    {repCalendarLinks.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                        {l.is_default ? ' (default)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <TouchList touches={touches} timezone={meetingTimezone} />

            {!meeting.show_is_general && (
              <div className="pt-2.5 border-t border-border/50 space-y-3">
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <span className="flex items-center gap-2 text-sm">
                    <CalendarCheck className="w-4 h-4 text-muted-foreground shrink-0" />
                    Booked a meeting
                  </span>
                  <Switch
                    checked={meetingBooked}
                    disabled={savingToggle === 'meeting_booked'}
                    onCheckedChange={(v) => handleToggle('meeting_booked', v)}
                  />
                </label>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <span className="flex items-center gap-2 text-sm">
                    <BellOff className="w-4 h-4 text-muted-foreground shrink-0" />
                    Stop reminders
                  </span>
                  <Switch
                    checked={remindersStopped}
                    disabled={savingToggle === 'reminders_stopped'}
                    onCheckedChange={(v) => handleToggle('reminders_stopped', v)}
                  />
                </label>
                <p className="text-[10px] text-muted-foreground">
                  {meetingBooked || remindersStopped
                    ? 'Reminders are stopped for this contact.'
                    : [meeting.reminder_1_sent_at, meeting.reminder_2_sent_at, meeting.reminder_3_sent_at].filter(Boolean).length > 0
                      ? `${[meeting.reminder_1_sent_at, meeting.reminder_2_sent_at, meeting.reminder_3_sent_at].filter(Boolean).length} of 3 reminders sent.`
                      : 'No reminders sent yet.'}
                </p>
              </div>
            )}
          </div>

          {isAdmin ? (
            <div className="pt-1 border-t border-border/50">
              <Label className="text-xs">Owning Rep</Label>
              <Select
                value={meeting.scanned_by_user_id || ''}
                onValueChange={handleReassign}
                disabled={reassigning}
              >
                <SelectTrigger className="h-10 md:text-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {reps.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">
                Changing this stops pending follow up reminders and, for a personal capture, moves it to the
                new rep's own bucket. Applies immediately, not part of Save Changes.
              </p>
            </div>
          ) : (
            <div className="text-[10px] text-muted-foreground">
              Captured by {meeting.scanned_by_name || 'an unknown rep'}.
            </div>
          )}

          <div className="h-4" />
        </div>
      </PageContainer>

      {/* True last element on the page (task: move below every other
          section, 2026-09-23), not just below the Save/Cancel row at the
          top: those are a persistent action bar, this is a one-time,
          destructive-ish action that belongs at the very end. */}
      <PageContainer className="px-4 pb-8">
        <div className="pt-2 border-t border-border/50">
          <Button
            type="button"
            variant="destructive"
            onClick={handleArchive}
            disabled={archiving}
            className="w-full min-h-10 text-sm font-semibold"
          >
            {archiving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Archive Contact
              </>
            )}
          </Button>
          <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
            Archives this contact, removing it from Home's active list. Restore it later from Deleted Contacts.
          </p>
        </div>
      </PageContainer>

      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
